<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import 'video.js/dist/video-js.css';
import videojs from 'video.js';
import { Draft } from '@/dashboard/types/draft';
import DraftUploadingLoader from '@/dashboard/vue/components/draft/_components/DraftUploadingLoader.vue';
import { showNotification } from '@/dashboard/vue/notifications/notification_actions';
import { useDraftStore } from '@/dashboard/store/draftStore';
import IconButton from '@/dashboard/vue/components/common/IconButton.vue';
import DraftHelp from '@/dashboard/vue/components/draft/_components/DraftHelp.vue';

const props = defineProps<{
  draft: Draft;
}>();

const draftStore = useDraftStore();
const { videoScaleDownMode } = storeToRefs(draftStore);

const isTouchDevice = () => {
  // Проверяет, поддерживает ли браузер сенсорные события (onTouchStart)
  // или имеет ли сенсорные точки (maxTouchPoints > 0).
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};
const videoPlayer = ref<any>(null);
const player = ref<any>(null);
const options = {
  autoplay: false,
  controls: true,
  width: props.draft.width,
  height: props.draft.height,
  poster: props.draft.image?.original_url,
  fluid: true,
  playbackRates: [0.5, 1, 1.5, 2],
  inactivityTimeout: isTouchDevice() ? 1000 : 500,
};
const showBottomHelp = ref(false);

let isDragging = false;
let lastX = 0;
let lastY = 0;
const wrapperEl = ref<HTMLElement | null>(null);

const updateVideoSize = () => {
  if (!wrapperEl.value) return;

  const videoJs = wrapperEl.value?.querySelector('.video-js') as HTMLElement;
  const videoJsTech = videoJs?.querySelector('.vjs-tech') as HTMLElement;
  const videoJsPoster = videoJs?.querySelector('.vjs-poster') as HTMLElement;

  if (!videoJsTech || !videoJsPoster) return;

  const rawWidth = videoScaleDownMode.value ? props.draft.width / 2 : props.draft.width;
  const rawHeight = videoScaleDownMode.value ? props.draft.height / 2 : props.draft.height;

  const maxWidth = window.innerWidth;
  const maxHeight = window.innerHeight;

  const width = Math.min(rawWidth, maxWidth);
  const height = Math.min(rawHeight, maxHeight);

  videoJsTech.style.width = width + 'px';
  videoJsTech.style.height = height + 'px';

  videoJsPoster.style.width = width + 'px';
  videoJsPoster.style.height = height + 'px';
};

const showBottomHelpClick = () => {
  showBottomHelp.value = !showBottomHelp.value;
};

watch(props, () => {
  if (props.draft.file) {
    setTimeout(() => {
      player.value = videojs(videoPlayer.value, options, () => {});
      updateVideoSize();
    }, 100);
  }
});

watch(videoScaleDownMode, () => {
  updateVideoSize();
});

// Zoom & Pan (Figma-style)
const scale = ref(1);
const originX = ref(0);
const originY = ref(0);

onMounted(() => {
  if (props.draft.file) {
    player.value = videojs(videoPlayer.value, options, () => {});
  }

  wrapperEl.value = document.querySelector('.zoom-wrapper') as HTMLElement;
  const videoEl = wrapperEl.value?.querySelector('video') as HTMLElement;
  const videoJs = wrapperEl.value?.querySelector('.video-js') as HTMLElement;

  if (!wrapperEl.value || !videoEl || !videoJs) return;

  updateVideoSize();

  setTimeout(() => {
    updateVideoSize();
  }, 100);

  const updateTransform = () => {
    const wrapper = wrapperEl.value!;
    const videoW = videoEl.offsetWidth;
    const videoH = videoEl.offsetHeight;

    // Scaled size
    const scaledW = videoW * scale.value;
    const scaledH = videoH * scale.value;

    // Maximum allowed offsets
    const maxOffsetX = Math.max((scaledW - wrapper.offsetWidth) / 2, 0);
    const maxOffsetY = Math.max((scaledH - wrapper.offsetHeight) / 2, 0);

    // Clamp origin to ensure edges do not move inside wrapper
    originX.value = Math.min(Math.max(originX.value, -maxOffsetX), maxOffsetX);
    originY.value = Math.min(Math.max(originY.value, -maxOffsetY), maxOffsetY);

    videoEl.style.transform = `translate(${originX.value}px, ${originY.value}px) scale(${scale.value})`;
  };

  const showZoom = () => {
    const percent = Math.round(scale.value * 100);
    showNotification('fast_type', {
      title: `Zoom ${percent}%`,
    });
  };

  // Wheel Zoom (center-based)
  wrapperEl.value.addEventListener('wheel', (e) => {
    // если зажаты ⌘ (macOS) или Ctrl (Windows) — блокируем нативный зум браузера
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
    } else {
      return;
    }

    // если видео не началось или на паузе — зум видео запрещён
    if (!videoJs.classList.contains('vjs-has-started')) {
      return;
    }

    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    scale.value = Math.min(Math.max(scale.value + delta, 0.3), 5);

    if (scale.value === 1) {
      originX.value = 0;
      originY.value = 0;
    }

    updateTransform();
    showZoom();
  });

  // Space + Drag = Pan
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Space') {
      e.preventDefault(); // prevent video from pausing/playing
      wrapperEl.value!.classList.add('hand-tool');
    }
  });

  window.addEventListener('keyup', (e) => {
    if (e.code === 'Space') wrapperEl.value!.classList.remove('hand-tool');
  });

  window.addEventListener('keydown', (e) => {
    const isZoomKey = e.key === '+' || e.key === '=' || e.key === '-' || e.key === '0';

    if (!e.metaKey || !isZoomKey) return;

    e.preventDefault();

    if (!videoJs.classList.contains('vjs-has-started')) {
      return;
    }

    if (e.key === '+' || e.key === '=') {
      scale.value = Math.min(scale.value + 0.1, 5);
    } else if (e.key === '-') {
      scale.value = Math.max(scale.value - 0.1, 0.3);
    } else if (e.key === '0') {
      scale.value = 1;
      originX.value = 0;
      originY.value = 0;
    }

    updateTransform();
    showZoom();
  });

  wrapperEl.value.addEventListener('mousedown', (e) => {
    if (!wrapperEl.value!.classList.contains('hand-tool') || scale.value <= 1) {
      isDragging = false;
      return;
    }

    e.preventDefault(); // prevent video from pausing/playing on drag

    isDragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
  });

  window.addEventListener('mouseup', () => (isDragging = false));

  window.addEventListener('mousemove', (e) => {
    if (!isDragging || scale.value <= 1) return;

    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;

    lastX = e.clientX;
    lastY = e.clientY;

    originX.value += dx;
    originY.value += dy;

    updateTransform();
  });

  window.addEventListener('resize', (e) => {
    updateVideoSize();
  });
});
</script>

<template>
  <div v-if="draft.file" class="text-caption-s flex flex-1 items-center justify-center">
    <div class="flex flex-1 justify-center bg-gray-100 dark:bg-gray-975">
      <div :style="{ width: 'calc(100vw)', height: 'calc(100vh)' }" class="relative h-full w-full">
        <div class="absolute h-full w-full">
          <div class="zoom-wrapper">
            <video
              ref="videoPlayer"
              :poster="props.draft.image?.original_url"
              :controls="false"
              playsinline
              class="video-js vjs-theme-fantasy !h-full !w-full !pt-zero"
            >
              <source :src="draft.file.original_url" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </div>

    <div v-if="draft" class="fixed bottom-xm right-xm max-tablet:hidden">
      <IconButton ref="showBottomHelpBtn" :shadow="false" :action="showBottomHelpClick" :active="showBottomHelp">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.75329 19.5 0.5 15.2467 0.5 10C0.5 4.75329 4.75329 0.5 10 0.5C15.2467 0.5 19.5 4.75329 19.5 10Z"
          />
          <path d="M10 9L10 16" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M10 4L10 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </IconButton>

      <DraftHelp v-if="showBottomHelp" :draft="draft"></DraftHelp>
    </div>
  </div>

  <DraftUploadingLoader v-else />
</template>

<style lang="scss">
video {
  background-color: rgba(252, 252, 252, 1) !important;

  &.vjs-tech {
    top: auto !important;
    left: auto !important;
  }
}

.dark video {
  background-color: rgba(19, 19, 19, 1) !important;
}

.vjs-poster {
  margin: auto;
}

.vjs-has-started.vjs-user-inactive .vjs-control-bar {
  transition: opacity 0.2s ease-out !important; /* Faster fade out */
}

.vjs-theme-fantasy {
  --vjs-theme-fantasy--primary: #0059ff;
  --vjs-theme-fantasy--secondary: #fff;

  --vjs-theme-fantasy--panel-color: rgba(246, 246, 246, 0.7);
  --vjs-theme-fantasy--panel-font-color: #505050;
  --vjs-theme-fantasy--panel-progress-loading-color: #8d8d8d;
  --vjs-theme-fantasy--panel-progrees-background-color: #c5c5c5;
  --vjs-theme-fantasy--panel-progress-pin-color: #181818;
}

.dark .vjs-theme-fantasy {
  --vjs-theme-fantasy--panel-color: rgba(40, 40, 40, 0.7);
  --vjs-theme-fantasy--panel-font-color: #b7b7b7;
  --vjs-theme-fantasy--panel-progress-loading-color: #6d6d6d;
  --vjs-theme-fantasy--panel-progrees-background-color: #282828;

  .vjs-big-play-button {
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M60 30C60 46.5685 46.5685 60 30 60C13.4315 60 0 46.5685 0 30C0 13.4315 13.4315 0 30 0C46.5685 0 60 13.4315 60 30ZM26.8248 40.8193L41.3847 31.6947C42.6353 30.911 42.6353 29.089 41.3847 28.3053L26.8248 19.1807C25.4928 18.3459 23.7627 19.3034 23.7627 20.8754L23.7627 39.1246C23.7627 40.6966 25.4928 41.6541 26.8248 40.8193Z' fill='%23000000' /%3E%3C/svg%3E%0A");
    filter: drop-shadow(0 0px 12px rgba(255, 255, 255, 0.3));
  }
}

.vjs-theme-fantasy .vjs-big-play-button {
  width: 60px;
  height: 60px;
  margin-top: -30px;
  margin-left: -30px;
  font-size: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M60 30C60 46.5685 46.5685 60 30 60C13.4315 60 0 46.5685 0 30C0 13.4315 13.4315 0 30 0C46.5685 0 60 13.4315 60 30ZM26.8248 40.8193L41.3847 31.6947C42.6353 30.911 42.6353 29.089 41.3847 28.3053L26.8248 19.1807C25.4928 18.3459 23.7627 19.3034 23.7627 20.8754L23.7627 39.1246C23.7627 40.6966 25.4928 41.6541 26.8248 40.8193Z' fill='%23F6F6F6' /%3E%3C/svg%3E%0A");
  border: none;
  background-color: transparent;
  opacity: 1;
  color: transparent;
  filter: drop-shadow(0 0px 12px rgba(0, 0, 0, 0.3));

  &:after {
    content: none;
  }
}

.vjs-theme-fantasy:hover .vjs-big-play-button,
.vjs-theme-fantasy.vjs-big-play-button:focus {
  opacity: 1;
  background-color: transparent;
}

.vjs-theme-fantasy .vjs-control-bar {
  height: 48px;
  background-color: var(--vjs-theme-fantasy--panel-color);
  color: var(--vjs-theme-fantasy--panel-font-color);
  font-family: 'IBM Plex Mono', monospace;
  bottom: 32px;
  max-width: 800px;
  margin: auto;
  border-radius: 12px;
  justify-self: center;
}

.vjs-theme-fantasy .vjs-button > .vjs-icon-placeholder::before {
  line-height: 48px;
}

.vjs-theme-fantasy .vjs-time-control {
  line-height: 48px;
}

/* Play Button */
.vjs-theme-fantasy .vjs-play-control,
.vjs-theme-fantasy .vjs-picture-in-picture-control,
.vjs-theme-fantasy .vjs-fullscreen-control,
.vjs-theme-fantasy .vjs-mute-control {
  font-size: 13px;
  position: relative;
}

@media (max-width: 1093px) {
  .vjs-theme-fantasy .vjs-picture-in-picture-control {
    display: none;
  }

  .vjs-theme-fantasy .vjs-volume-panel {
    display: none;
  }

  .vjs-theme-fantasy .vjs-control-bar {
    width: calc(100% - 32px);
  }
}

.vjs-theme-fantasy .vjs-volume-panel {
  order: 4;

  .vjs-volume-control {
    height: 100% !important;
    display: flex;
    align-items: center;

    .vjs-mouse-display {
      .vjs-volume-tooltip {
        background-color: var(--vjs-theme-fantasy--panel-color);
        color: var(--vjs-theme-fantasy--panel-font-color);
        font-family: 'IBM Plex Mono', monospace;
        font-size: 13px !important;
        top: -50px;
      }
    }

    .vjs-volume-bar {
      height: 4px;
      border-radius: 4px;
      background-color: var(--vjs-theme-fantasy--panel-progress-loading-color);

      .vjs-volume-level {
        height: 4px;
        border-radius: 4px;
        background-color: var(--vjs-theme-fantasy--primary);

        &:before {
          height: 8px;
          width: 8px;
          content: '';
          background-color: var(--vjs-theme-fantasy--panel-progress-pin-color);
          border-radius: 8px;
          top: -2px;
        }
      }
    }
  }
}

.vjs-theme-fantasy .vjs-progress-control {
  font-size: 14px;

  &:hover {
    .vjs-progress-holder {
      font-size: 1em;
    }
  }

  .vjs-progress-holder {
    margin: 0;
    background-color: var(--vjs-theme-fantasy--panel-progrees-background-color);
    border-radius: 4px;
    height: 4px;

    .vjs-load-progress {
      background: var(--vjs-theme-fantasy--panel-progress-loading-color);
      border-radius: 4px;

      div {
        border-radius: 4px;
      }
    }

    .vjs-play-progress {
      background-color: var(--vjs-theme-fantasy--primary);
      border-radius: 4px;

      &:before {
        height: 8px;
        width: 8px;
        content: '';
        background-color: var(--vjs-theme-fantasy--panel-progress-pin-color);
        border-radius: 8px;
        top: -2px;
      }
    }
  }
}

.vjs-theme-fantasy .vjs-fullscreen-control {
  order: 6;
}

.vjs-theme-fantasy .vjs-remaining-time {
  font-size: 16px;
}

.vjs-theme-fantasy .vjs-playback-rate {
  .vjs-playback-rate-value {
    font-size: 16px;
    line-height: 48px;
  }
}

.vjs-menu-button-popup .vjs-menu {
  margin-bottom: 48px;

  .vjs-menu-content {
    bottom: 0;
    background-color: var(--vjs-theme-fantasy--panel-color);

    .vjs-menu-item {
      color: var(--vjs-theme-fantasy--panel-font-color);
      font-family: 'IBM Plex Mono', monospace;
      font-size: 12px !important;

      &:hover {
        background-color: var(--vjs-theme-fantasy--panel-progrees-background-color);
      }

      &.vjs-selected {
        background-color: var(--vjs-theme-fantasy--panel-progress-loading-color);
      }
    }
  }
}

.video-js {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

.video-js .vjs-time-tooltip {
  top: -50px;
  background-color: var(--vjs-theme-fantasy--panel-color);
  color: var(--vjs-theme-fantasy--panel-font-color);
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px !important;
}

.zoom-wrapper {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.zoom-wrapper.hand-tool {
  cursor: grab;

  video {
    pointer-events: none;
  }
}

.zoom-wrapper.hand-tool:active {
  cursor: grabbing;
}

.zoom-wrapper video {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: center center;
  will-change: transform;
  outline: none;
}
</style>
